import React, { useEffect, useRef } from "react";
import { FocusScope, usePreventScroll } from "react-aria";

import { SubmenuBlock } from "@reactivated";

import { Themed } from "../../styles/themes";
import { useHeaderContext } from "./HeaderContext";
import { SubmenuContent } from "./SubmenuContent";
import { useSubpanelKeyboardNav } from "./hooks/useSubpanelKeyboardNav";

import styles from "./SubmenuPanel.module.scss";

interface Props {
    menuId: string;
    items: NonNullable<SubmenuBlock["items"]>;
}

export const SubmenuPanel = ({ menuId, items }: Props) => {
    const { activeSubmenu, setActiveSubmenu } = useHeaderContext();
    const isOpen = activeSubmenu === menuId;
    const panelRef = useRef<HTMLDivElement>(null);

    usePreventScroll({ isDisabled: !isOpen });

    useEffect(() => {
        if (!isOpen) return;

        const handleFocusOut = (event: FocusEvent) => {
            /* v8 ignore else -- @preserve */
            if (
                panelRef.current &&
                event.relatedTarget instanceof Node &&
                !panelRef.current.contains(event.relatedTarget)
            ) {
                const trigger = document.querySelector(
                    `[aria-controls="${menuId}"]`,
                );
                if (trigger && trigger.contains(event.relatedTarget)) {
                    return;
                }
                setActiveSubmenu(null);
            }
        };

        panelRef.current?.addEventListener("focusout", handleFocusOut);

        const currentPanel = panelRef.current;
        return () => {
            currentPanel?.removeEventListener("focusout", handleFocusOut);
        };
    }, [isOpen, menuId, setActiveSubmenu]);

    const { onKeyDown: handleKeyDown } = useSubpanelKeyboardNav(panelRef);

    if (!isOpen) return null;

    return (
        <FocusScope restoreFocus contain={false}>
            <Themed
                theme="offWhite"
                ref={panelRef}
                id={menuId}
                className={styles.panel}
                onKeyDown={handleKeyDown}
                onMouseLeave={() => setActiveSubmenu(null)}
            >
                <SubmenuContent items={items} />
            </Themed>
        </FocusScope>
    );
};
